import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { equipment as equipmentApi } from '../../services/api';

/* Peças partilhadas pelo ecrã de administração da biblioteca. */

export const useDebounced = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

/** As mensagens de erro de uma resposta do servidor: { message, errors: [...] }. */
export const errorList = (error, fallback = 'Não foi possível concluir a operação.') => {
  const data = error?.response?.data;
  if (Array.isArray(data?.errors) && data.errors.length > 0) return data.errors;
  if (typeof data?.message === 'string' && data.message) return [data.message];
  if (error?.response?.status === 403) return ['Sem permissão para gerir a biblioteca de exercícios.'];
  return [fallback];
};

/** O código → a etiqueta, a partir de uma lista { code, label } do vocabulário. */
export const labelOf = (list, code, fallback = '') => (list || []).find((x) => x.code === code)?.label ?? (code || fallback);

export const SOURCE_LABELS = { catalog: 'Catálogo', custom: 'Do ginásio' };

/** Todas as máquinas do ginásio (para filtros e para escolher no editor): páginas até acabar. */
export const useAllEquipment = (reloadKey = 0) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const all = [];
        for (let page = 1; page <= 20; page += 1) {
          const { data } = await equipmentApi.search({ page, pageSize: 100, includeInactive: true });
          all.push(...data.items);
          if (all.length >= data.total || data.items.length === 0) break;
        }
        if (alive) setItems(all);
      } catch {
        if (alive) setItems([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [reloadKey]);

  return { items, loading };
};

export function Thumb({ src, alt = '', icon = '🏋️' }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  return (
    <div className="lib-thumb" aria-hidden={alt ? undefined : 'true'}>
      {src && !broken ? <img src={src} alt={alt} loading="lazy" onError={() => setBroken(true)} /> : <span>{icon}</span>}
    </div>
  );
}

export function Chip({ tone, children, title }) {
  return <span className={`lib-chip${tone ? ` ${tone}` : ''}`} title={title}>{children}</span>;
}

export function SourceChips({ item }) {
  return (
    <>
      <Chip tone={item.source === 'catalog' ? 'accent' : undefined}>{SOURCE_LABELS[item.source] || item.source}</Chip>
      {item.source === 'catalog' && item.isCustomized && <Chip tone="warn" title="Editado no ginásio: o catálogo já não lhe toca">Editado</Chip>}
      {!item.isActive && <Chip tone="bad">Desativado</Chip>}
    </>
  );
}

export function Field({ label, hint, error, children, htmlFor }) {
  return (
    <div className="lib-field">
      {label && <label htmlFor={htmlFor}>{label}</label>}
      {children}
      {hint && !error && <span className="lib-hint">{hint}</span>}
      {error && <span className="lib-error-text" role="alert">{error}</span>}
    </div>
  );
}

export function Switch({ checked, onChange, disabled, onLabel = 'Sim', offLabel = 'Não', label }) {
  return (
    <label className="lib-switch">
      <input type="checkbox" role="switch" checked={!!checked} disabled={disabled} aria-label={label}
        onChange={(e) => onChange(e.target.checked)} />
      <span className="track" aria-hidden="true" />
      <span>{checked ? onLabel : offLabel}</span>
    </label>
  );
}

/**
 * Janela com foco preso, Esc para fechar e a página de trás sem rolar. Em telemóvel ocupa o ecrã
 * todo (folha); em ecrã largo é uma janela centrada.
 */
export function Modal({ title, onClose, children, footer, narrow = false, labelId }) {
  const autoId = useId();
  const headingId = labelId || `lib-dialog-${autoId}`;
  const dialogRef = useRef(null);
  const previouslyFocused = useRef(null);

  useEffect(() => {
    previouslyFocused.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const first = dialogRef.current?.querySelector('input, select, textarea, button:not([data-close])');
    (first || dialogRef.current)?.focus?.();
    return () => {
      document.body.style.overflow = previousOverflow;
      previouslyFocused.current?.focus?.();
    };
  }, []);

  const onKeyDown = useCallback((event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = dialogRef.current?.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }, [onClose]);

  return createPortal(
    <div className="lib-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={dialogRef} className={`lib-dialog${narrow ? ' narrow' : ''}`} role="dialog" aria-modal="true"
        aria-labelledby={headingId} tabIndex={-1} onKeyDown={onKeyDown}>
        <div className="lib-dialog-head">
          <h2 id={headingId}>{title}</h2>
          <button type="button" className="lib-btn icon ghost" data-close aria-label="Fechar" onClick={onClose}>✕</button>
        </div>
        <div className="lib-dialog-body">{children}</div>
        {footer && <div className="lib-dialog-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

export function ConfirmDialog({ title, message, confirmLabel = 'Confirmar', danger = false, busy = false, onConfirm, onCancel }) {
  const id = useId();
  return createPortal(
    <div className="lib-overlay" style={{ zIndex: 1100 }} onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="lib-dialog confirm" role="alertdialog" aria-modal="true" aria-labelledby={`${id}-t`} aria-describedby={`${id}-m`}
        onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onCancel(); } }}>
        <div className="lib-dialog-head"><h2 id={`${id}-t`}>{title}</h2></div>
        <div className="lib-dialog-body"><p id={`${id}-m`} style={{ margin: 0 }}>{message}</p></div>
        <div className="lib-dialog-foot">
          <button type="button" className="lib-btn" onClick={onCancel} autoFocus disabled={busy}>Cancelar</button>
          <button type="button" className={`lib-btn ${danger ? 'danger' : 'primary'}`} onClick={onConfirm} disabled={busy}>
            {busy ? 'A processar…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Lista ordenada de textos (passos de execução, erros comuns): acrescentar, apagar e mudar de lugar. */
export function ListEditor({ items, onChange, max, addLabel, placeholder, maxLength, ariaLabel }) {
  const update = (index, value) => onChange(items.map((item, i) => (i === index ? value : item)));
  const remove = (index) => onChange(items.filter((_, i) => i !== index));
  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <div>
      {items.length > 0 && (
        <ol className="lib-listedit" aria-label={ariaLabel}>
          {items.map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={index}>
              <span className="num" aria-hidden="true">{index + 1}</span>
              <textarea className="lib-textarea" rows={2} value={item} maxLength={maxLength} placeholder={placeholder}
                aria-label={`${ariaLabel} ${index + 1}`} onChange={(e) => update(index, e.target.value)} />
              <span className="ops">
                <button type="button" className="lib-btn small icon ghost" aria-label="Subir" disabled={index === 0} onClick={() => move(index, -1)}>↑</button>
                <button type="button" className="lib-btn small icon ghost" aria-label="Descer" disabled={index === items.length - 1} onClick={() => move(index, 1)}>↓</button>
                <button type="button" className="lib-btn small icon ghost" aria-label="Apagar" onClick={() => remove(index)}>✕</button>
              </span>
            </li>
          ))}
        </ol>
      )}
      <button type="button" className="lib-btn small" style={{ marginTop: 8 }} disabled={items.length >= max}
        onClick={() => onChange([...items, ''])}>+ {addLabel}</button>
      {items.length >= max && <span className="lib-hint" style={{ marginLeft: 8 }}>Máximo de {max}.</span>}
    </div>
  );
}

/** Nomes alternativos: escreve-se e carrega-se em Enter (ou vírgula) para acrescentar. */
export function ChipsInput({ values, onChange, max = 12, placeholder, ariaLabel, maxLength = 60 }) {
  const [draft, setDraft] = useState('');

  const add = () => {
    const text = draft.trim();
    if (!text) return;
    const exists = values.some((v) => v.trim().toLowerCase() === text.toLowerCase());
    if (!exists && values.length < max) onChange([...values, text]);
    setDraft('');
  };

  return (
    <div>
      {values.length > 0 && (
        <div className="lib-chips" style={{ marginBottom: 6 }}>
          {values.map((value) => (
            <span key={value} className="lib-chip">
              {value}
              <button type="button" aria-label={`Remover ${value}`} onClick={() => onChange(values.filter((v) => v !== value))}>×</button>
            </span>
          ))}
        </div>
      )}
      <input className="lib-input" value={draft} maxLength={maxLength} placeholder={placeholder} aria-label={ariaLabel}
        disabled={values.length >= max}
        onChange={(e) => setDraft(e.target.value.replace(';', ','))}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } }}
        onBlur={add} />
    </div>
  );
}
